import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, ArrowUpRight, ArrowDownRight, Minus, Calendar, FileText, Filter } from 'lucide-react';

const EVENT_TYPE_LABELS = {
  contract_client: 'Contratto cliente',
  contract_modification: 'Perizia',
  subcontract: 'Subappalto',
  supplier_invoice: 'Fattura fornitore',
  supplier_payment: 'Pagamento fornitore',
  client_payment: 'Incasso cliente',
  client_sal: 'SAL cliente',
  supplier_sal: 'SAL subappalto',
};

const METRIC_CARDS = [
  { key: 'ricavi_contrattualizzati', label: 'Ricavi contrattualizzati', color: 'text-emerald-700' },
  { key: 'ricavi_contabilizzati', label: 'Ricavi contabilizzati (SAL)', color: 'text-emerald-600' },
  { key: 'ricavi_fatturati', label: 'Ricavi fatturati', color: 'text-emerald-500', unavailable: true },
  { key: 'incassi_cliente', label: 'Incassi cliente', color: 'text-blue-700' },
  { key: 'costi_affidati', label: 'Costi affidati', color: 'text-red-700' },
  { key: 'costi_contabilizzati', label: 'Costi contabilizzati (SAL)', color: 'text-red-600' },
  { key: 'costi_fatturati', label: 'Costi fatturati', color: 'text-red-500' },
  { key: 'pagamenti_fornitori', label: 'Pagamenti fornitori', color: 'text-orange-700' },
  { key: 'margine_teorico', label: 'Margine teorico', color: 'text-purple-700' },
  { key: 'margine_contabilizzato', label: 'Margine contabilizzato', color: 'text-purple-600' },
  { key: 'margine_fatturato', label: 'Margine fatturato', color: 'text-purple-500', unavailable: true },
  { key: 'cash_flow', label: 'Cash flow', color: 'text-cyan-700' },
];

const DIRECTION_ICON = {
  inflow: <ArrowDownRight className="w-4 h-4 text-emerald-600" />,
  outflow: <ArrowUpRight className="w-4 h-4 text-red-600" />,
  neutral: <Minus className="w-4 h-4 text-[#737373]" />,
};

const ProjectEconomicTimelineTab = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    event_types: '',      // CSV
    date_from: '',
    date_to: '',
    order: 'desc',
    group_by_month: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        order: filters.order,
        page_size: 200,
      };
      if (filters.group_by_month) params.group_by_month = true;
      if (filters.event_types) params.event_types = filters.event_types;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const res = await api.get(
        `/projects/${projectId}/economic-timeline`,
        { params }
      );
      setData(res.data);
    } catch (e) {
      console.error('Timeline load error', e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
  }, [projectId, filters]);

  const metrics = data?.metrics;

  const renderMetricCard = (m) => {
    const val = metrics?.[m.key];
    const isUnavailable = m.unavailable || val === null || val === undefined;
    return (
      <div key={m.key} className="rounded-lg border border-[#DCDCDC] p-3 bg-white" data-testid={`metric-${m.key}`}>
        <p className="text-xs text-[#737373]">{m.label}</p>
        {isUnavailable ? (
          <p className="text-sm text-[#A0A0A0] mt-1 italic">dato non disponibile</p>
        ) : (
          <p className={`text-lg font-semibold mt-1 ${m.color}`}>{formatCurrency(val)}</p>
        )}
      </div>
    );
  };

  const renderEventRow = (e) => (
    <tr key={e.event_id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]" data-testid={`timeline-event-${e.event_id}`}>
      <td className="px-2 py-2 whitespace-nowrap text-xs text-[#737373]">{formatDate(e.occurred_at)}</td>
      <td className="px-2 py-2 whitespace-nowrap">{DIRECTION_ICON[e.direction] || DIRECTION_ICON.neutral}</td>
      <td className="px-2 py-2">
        <span className="text-xs px-1.5 py-0.5 rounded bg-[#F5F5F5] text-[#3C3C3C]">
          {EVENT_TYPE_LABELS[e.event_type] || e.event_type}
        </span>
      </td>
      <td className="px-2 py-2 text-sm max-w-[300px] truncate" title={e.description}>{e.description || '—'}</td>
      <td className="px-2 py-2 text-xs text-[#737373]">{e.subject_name || '—'}</td>
      <td className="px-2 py-2 text-xs text-[#737373]">{e.source_document_number || '—'}</td>
      <td className={`px-2 py-2 text-right font-medium ${
        e.direction === 'inflow' ? 'text-emerald-700' :
        e.direction === 'outflow' ? 'text-red-700' : 'text-[#3C3C3C]'
      }`}>
        {e.direction === 'outflow' ? '-' : e.direction === 'inflow' ? '+' : ''}
        {formatCurrency(e.amount || 0)}
      </td>
    </tr>
  );

  const eventTypeOptions = useMemo(() => Object.entries(EVENT_TYPE_LABELS), []);

  return (
    <div className="space-y-4" data-testid="economic-timeline-panel">
      {/* METRICHE */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {METRIC_CARDS.map(renderMetricCard)}
      </div>

      {/* FILTRI */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filtri
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs text-[#737373]">Tipo evento</label>
              <Select
                value={filters.event_types || 'all'}
                onValueChange={(v) => setFilters(f => ({ ...f, event_types: v === 'all' ? '' : v }))}
              >
                <SelectTrigger data-testid="filter-event-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {eventTypeOptions.map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#737373]">Da</label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <label className="text-xs text-[#737373]">A</label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                data-testid="filter-date-to"
              />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Ordine</label>
              <Select
                value={filters.order}
                onValueChange={(v) => setFilters(f => ({ ...f, order: v }))}
              >
                <SelectTrigger data-testid="filter-order">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Più recenti prima</SelectItem>
                  <SelectItem value="asc">Meno recenti prima</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant={filters.group_by_month ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilters(f => ({ ...f, group_by_month: !f.group_by_month }))}
                data-testid="toggle-group-month"
                className="w-full"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {filters.group_by_month ? 'Vista piatta' : 'Raggruppa per mese'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TIMELINE */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Eventi ({data?.total_events || 0})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-[#737373]">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Caricamento timeline...
            </div>
          ) : !data ? (
            <div className="py-10 text-center text-[#737373] text-sm">
              Impossibile caricare la timeline
            </div>
          ) : filters.group_by_month ? (
            /* GROUPED VIEW */
            <div className="space-y-4">
              {(data.grouped || []).map((g) => (
                <div key={g.month} className="border border-[#DCDCDC] rounded-md" data-testid={`month-group-${g.month}`}>
                  <div className="flex items-center justify-between px-3 py-2 bg-[#F5F5F5] border-b border-[#DCDCDC]">
                    <span className="font-medium">{g.month}</span>
                    <div className="text-xs flex gap-3">
                      <span className="text-emerald-700">↓ {formatCurrency(g.subtotals.inflow)}</span>
                      <span className="text-red-700">↑ {formatCurrency(g.subtotals.outflow)}</span>
                      <span className={g.subtotals.net >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                        = {formatCurrency(g.subtotals.net)}
                      </span>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>{g.events.map(renderEventRow)}</tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : (
            /* FLAT VIEW */
            <div className="border border-[#DCDCDC] rounded-md overflow-auto max-h-[600px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#F5F5F5] border-b border-[#DCDCDC]">
                  <tr>
                    <th className="text-left px-2 py-2 text-xs">Data</th>
                    <th className="text-left px-2 py-2 text-xs">Dir</th>
                    <th className="text-left px-2 py-2 text-xs">Tipo</th>
                    <th className="text-left px-2 py-2 text-xs">Descrizione</th>
                    <th className="text-left px-2 py-2 text-xs">Soggetto</th>
                    <th className="text-left px-2 py-2 text-xs">Documento</th>
                    <th className="text-right px-2 py-2 text-xs">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.events || []).length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-[#737373] text-sm">
                      Nessun evento nel periodo selezionato
                    </td></tr>
                  ) : (data.events || []).map(renderEventRow)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectEconomicTimelineTab;
